import axios from 'axios'

const account = {
  namespaced: true,

  state() {
    return {
      id: null,
      is_guest: false,
      is_supervisor: null,
      email: null,
      name: null,
      name_kana: null,
      contact: null,
      office: null,
      department: null,
      company: {
        name: null,
        name_kana: null,
        licence_type: null,
        licence_number: null,
        licence_update: null,
        postal_code: null,
        prefecture_code: null,
        address1: null,
        address2: null,
        tel: null,
        fax: null,
      },
      guest: {
        issuance_request_id: null,
      },
      management_company_code: null,
      guest_type: null,
      two_step: false,
      errors: {},
    };
  },

  mutations: {
    setData(state, data) {
      state.id = data.id;
      state.is_guest = false;
      state.is_supervisor = data.is_supervisor;
      state.email = data.email;
      state.name = data.name;
      state.name_kana = data.name_kana;
      state.contact = data.contact;
      state.office = data.office;
      state.department = data.department;
      state.company.name = data.company.name;
      state.company.name_kana = data.company.name_kana;
      state.company.licence_type = parseInt(data.company.licence_type);
      state.company.licence_number = data.company.licence_number;
      state.company.licence_update = data.company.licence_update;
      state.company.postal_code = data.company.postal_code;
      state.company.prefecture_code = parseInt(data.company.prefecture_code);
      state.company.address1 = data.company.address1;
      state.company.address2 = data.company.address2;
      state.company.tel = data.company.tel;
      state.company.fax = data.company.fax;
      state.guest.issuance_request_id = null;
    },
    setGuestData(state, data) {
      state.id = null;
      state.is_guest = true;
      state.is_supervisor = false;
      state.email = data.email;
      state.name = data.name;
      state.name_kana = data.name_kana;
      state.contact = data.contact;
      state.office = data.office;
      state.department = data.department;
      state.company.name = data.company_name;
      state.company.name_kana = data.company_name_kana;
      state.company.licence_type = parseInt(data.licence_type);
      state.company.licence_number = data.licence_number;
      state.company.licence_update = data.licence_update;
      state.company.postal_code = data.postal_code;
      state.company.prefecture_code = parseInt(data.prefecture_code);
      state.company.address1 = data.address1;
      state.company.address2 = data.address2;
      state.company.tel = data.tel;
      state.company.fax = data.fax;
      state.guest.issuance_request_id = data.issuance_request_id;
      state.management_company_code = data.management_company_code;
      state.guest_type = data.guest_type;
    },
    resetData(state) {
      state.id = null;
      state.is_guest = false;
      state.is_supervisor = null;
      state.email = null;
      state.name = null;
      state.name_kana = null;
      state.contact = null;
      state.office = null;
      state.department = null;
      state.company.name = null;
      state.company.name_kana = null;
      state.company.licence_type = null;
      state.company.licence_number = null;
      state.company.licence_update = null;
      state.company.postal_code = null;
      state.company.prefecture_code = null;
      state.company.address1 = null;
      state.company.address2 = null;
      state.company.tel = null;
      state.company.fax = null;
      state.guest.issuance_request_id = null;
      state.management_company_code = null;
      state.guest_type = null;
      state.two_step = false;
      state.errors = {}
    },

    changeTwoStepAuthentication(state, data) {
      state.two_step = data.is_required;
    },
    
    setErrors(state, errors) {
      state.errors = { ...errors };
    },
    resetErrors(state) {
      state.errors = {};
    },

    setGuestIssuanceRequestId(state, payload) {
      state.guest.issuance_request_id = payload.id;
    }
  },

  actions: {
    login({ commit }, payload = {}) {
      commit('resetErrors');
      return new Promise((resolve, reject) => {
        axios.get('/sanctum/csrf-cookie').then(() => {
          axios.post('/api/brokers/login', {
            email: payload.email,
            password: payload.password,
            token: payload.token,
          }).then((response) => {
            commit('setData', response.data.data);
            commit('changeTwoStepAuthentication', false);
            resolve(true);
          }).catch((error) => {
            let handled = false;
            if (error.response.status === 422) {
              commit('setErrors', error.response.data.errors);
              handled = true;
            } else if (error.response.status === 401) {
              if (error.response.data.is_required) {
                commit('changeTwoStepAuthentication', error.response.data);
                resolve(true);
              } else if (error.response.data.is_unmatched) {
                commit('setErrors', {token: ['確認コードが違います。']});
                handled = true;
              } else {
                commit('setErrors', { auth: ['メールアドレスまたはパスワードが違います。'] });
                handled = true;
              }
            }
            reject({ handled });
          });
        }).catch(() => reject());
      });
    },

    logout({ dispatch }) {
      return new Promise((resolve, reject) => {
        axios.post('/api/brokers/logout').then(() => {
          dispatch('initializeSearchForm', {}, { root: true });
          resolve(true);
        }).catch(() => reject());
      });
    },

    refresh({ commit }) {
      return new Promise((resolve, reject) => {
        axios.get('/api/brokers/me').then((response) => {
          commit('setData', response.data.data);
          resolve(true);
        }).catch(() => reject());
      });
    },

    authGuest({ commit }, payload) {
      commit('resetErrors');
      return new Promise((resolve, reject) => {
        axios.get('/sanctum/csrf-cookie').then(() => {
          axios.post('/api/guests/auth', {
            access_key: payload.access_key,
          }).then((response) => {
            commit('setGuestData', response.data.data);
            resolve(true);
          }).catch(() => reject());
        }).catch(() => reject());
      });
    },
    
    logoutGuest({ dispatch }) {
      return new Promise((resolve, reject) => {
        axios.post('/api/guests/logout').then(() => {
          dispatch('initializeSearchForm', {}, { root: true });
          resolve(true);
        }).catch(() => reject());
      });
    },

    validate({commit}, payload = {}) {
      return new Promise((resolve, reject) => {
        commit('resetErrors');
        if (payload.isTwoStep) {
          if (!payload.token) {
            commit('setErrors', {token: ['確認コードを入力してください。']});
            reject();
          }
        }
        resolve(true);
      });
    },

  }
};

export default account;
